import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';
import type { TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Box, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledTreeItem } from './styledTreeItem';
import { t } from 'i18next';
import { useDispatch } from 'react-redux';
import {
  selectExpandedNodes,
  selectSearchTerm,
  setExpandedNodes
} from '@redux/slices/managementTreeSlice';
import { useAppSelector } from '@redux/hooks';
import useAncestors from '@utils/hooks/useAncestors';

const StyledLabelContainer = styled(Box)(() => {
  return {
    display: 'flex',
    justifyContent: 'center',
    padding: '0.3125rem 1.125rem',
    fontFamily: tsoPqTheme.typography.openSans,
    fontWeight: 400,
  };
});

const StyledLabeWrapper = styled(Box)(() => {
  return {
    width: '100%',
    height: '34px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    lineHeight: '1.25rem',
    '& .MuiTypography-root': {
      margin: 'auto 0',
    },
  };
});

export const StyledIconButton = styled(IconButton)(() => {
  return {
    display: 'none',
    height: '1.5rem',
    width: '1.5rem',
    color: tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_accent,
    justifyContent: 'center',
    padding: '0.5rem',
    margin: 'auto',
    '&:focus': { outline: 'none' },
    '& svg': { fontSize: '1.5rem' },
  };
});

const StyledDescription = styled(Typography)(() => {
  return {
    margin: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8} auto auto 0`,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_hover,
    lineHeight: '0.875rem',
  };
});

const StyledSimpleTreeView = styled(SimpleTreeView)(() => {
  return {
    '&.MuiSimpleTreeView-root': {
      padding: '0.375rem 0',
      display: 'flex',
      flexDirection: 'column',
    },
  };
});

const buildItemsById = (
  nodes: TreeListItemTsoPqProps[],
  itemsById: Record<string, TreeListItemTsoPqProps> = {}
): Record<string, TreeListItemTsoPqProps> => {
  for (const node of nodes) {
    itemsById[node.id] = node;

    if (node.options) {
      buildItemsById(node.options, itemsById);
    }
  }

  return itemsById;
};

const arraysEqual = (a: readonly string[] = [], b: readonly string[] = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const TreeListLabelTsoPq = ({
  label,
  description,
  enableAdd = true,
  type,
  buttonAction,
}: {
  label: string;
  description: string | undefined;
  enableAdd: boolean | undefined;
  type: string;
  buttonAction: () => void;
}) => {
  const { t } = useTranslation();

  const tooltips = {
    substation: t('management.measurementPoint.create.button.tooltip'),
    measurmentPoint: t('management.measurementDevice.create.button.tooltip'),
  };
  return (
    <StyledLabelContainer>
      <StyledLabeWrapper>
        <Typography>{label}</Typography>
        {description && <StyledDescription>{description}</StyledDescription>}
      </StyledLabeWrapper>
      {enableAdd && (
        <PermissionGate roles={userOrSupportRestriction}>
          <IconButtonTsoPq
            sizeVariant={'m'}
            tooltip={tooltips[type as keyof typeof tooltips]}
            buttonType="transparentPrimaryNoEffect"
            icon={faPlus}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
              buttonAction();
            }}
          />
        </PermissionGate>
      )}
    </StyledLabelContainer>
  );
};

const TreeListItemTsoPq = ({
  options,
  buttonAction,
}: {
  options: TreeListItemTsoPqProps[] | undefined;
  buttonAction: (type: string, id: string) => void;
}) => {
  return (
    options?.map((option: TreeListItemTsoPqProps) => (
      <Box key={`${option.id}-${option.type}`}>
        <StyledTreeItem
          itemId={`${option.id}`}
          label={
            <TreeListLabelTsoPq
              type={option.type}
              enableAdd={option.type === 'substation' || option.type === 'measurmentPoint'}
              label={option.label}
              description={option.description}
              buttonAction={() => buttonAction(option.type, option.id)}
            />
          }
          leftIcon={false}
        >
          {option.options && (
            <TreeListItemTsoPq options={option.options} buttonAction={buttonAction} />
          )}
        </StyledTreeItem>
      </Box>
    )) ?? []
  );
};

const TreeItemViewTspPq = ({
  options = [],
  buttonAction,
  onSelection,
}: {
  options: TreeListItemTsoPqProps[] | undefined;
  buttonAction: (type: string, id: string) => void;
  onSelection: (label: string, id: string, entityId: number) => void;
}) => {
  const dispatch = useDispatch();
  const searchTerm = useAppSelector(selectSearchTerm);
  const persistedExpanded = useAppSelector(selectExpandedNodes);
  const ancestorsById = useAncestors(options);
  const itemsById = useMemo(() => buildItemsById(options ?? []), [options]);

  const {
    filteredOptions,
    matchedIds,
  } = useMemo(() => {
    if (!searchTerm?.trim() || !options) {
      return { filteredOptions: options ?? [], matchedIds: [] };
    }

    const matchedSet = new Set<string>();

    const recurse = (
      node: TreeListItemTsoPqProps
    ): TreeListItemTsoPqProps | null => {
      const nodeMatch = node.label.toLowerCase().includes(searchTerm.toLowerCase());

      let childOptions: TreeListItemTsoPqProps[] | undefined;

      if (node.options) {
        const children = node.options
          .map(recurse)
          .filter((c): c is TreeListItemTsoPqProps => c !== null);
        if (children.length) childOptions = children;
      }

      if (nodeMatch || childOptions) {
        if (nodeMatch) matchedSet.add(node.id);
        return { ...node, options: childOptions };
      }
      return null;
    };

    const filtered = options
      .map(recurse)
      .filter((c): c is TreeListItemTsoPqProps => c !== null);

    return { filteredOptions: filtered, matchedIds: Array.from(matchedSet) };
  }, [searchTerm, options]);

  useEffect(() => {
    if (!searchTerm?.trim()) {
      if (persistedExpanded.length > 0) dispatch(setExpandedNodes([]));
      return;
    }
    const ancestorSet = new Set<string>();
    for (const matchedId of matchedIds) {
      const ancestors = ancestorsById[matchedId];
      if (!ancestors) continue;
      for (const ancestor of ancestors) {
        ancestorSet.add(ancestor);
      }
    }

    const newExpanded = [...matchedIds, ...ancestorSet];
    if (!arraysEqual(newExpanded, persistedExpanded)) {
      dispatch(setExpandedNodes(newExpanded));
    }
  }, [searchTerm, matchedIds, filteredOptions]);

  return (
    <StyledSimpleTreeView
      expandedItems={persistedExpanded}
      onItemClick={(_, itemId) => {
        const selectedItem = itemsById[itemId];

        if (!selectedItem) {
          return;
        }

        onSelection(selectedItem.label, selectedItem.id, selectedItem.entityId);
      }}
      onExpandedItemsChange={(_, ids) => {
        dispatch(setExpandedNodes(ids));
      }}
    >
      {filteredOptions && filteredOptions.length > 0 ? (
        <TreeListItemTsoPq
          options={filteredOptions}
          buttonAction={buttonAction}
        />
      ) : (
        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          {`❌ ${t('searchfield.noResult')}`}
        </Typography>
      )}
    </StyledSimpleTreeView>
  );
};

export default TreeItemViewTspPq;
